import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CheckIcon, SparklesIcon, ShieldCheckIcon } from 'lucide-react';
// The "calm" payoff beat — where TwelveTabProblem piles up cluttered tabs, this
// resolves to a clean, reassuring stack of "Handled" cards. Each card is one
// thing Giga noticed and finished on its own — overdue invoices, a low-stock
// reorder, meeting follow-ups, an expiring contract — so visitors see the
// breadth of proactive work, not just a single scripted demo. The cards cycle
// on a timer over a softly stacked backdrop so it feels like there's always
// another one Giga has quietly taken care of.
type HandledItem = {
  id: string;
  title: string;
  body: string;
  // The connected tools this job touched — shown as small brand chips so the
  // card reads as real cross-tool work, not a generic to-do.
  tools: string[];
  // How long Giga took, for the little footer stat.
  time: string;
};
const HANDLED: HandledItem[] = [{
  id: 'invoices',
  title: 'Found 3 overdue invoices',
  body: 'Drafted and sent follow-up emails to clients.',
  tools: ['quickbooks', 'gmail'],
  time: 'Done in 12s'
}, {
  id: 'restock',
  title: 'Spotted 5 products running low',
  body: 'Emailed your supplier a reorder and updated the count.',
  tools: ['shopify', 'gmail', 'googlesheets'],
  time: 'Done in 18s'
}, {
  id: 'followups',
  title: 'Recapped yesterday’s 4 calls',
  body: 'Logged the notes and created every follow-up task.',
  tools: ['zoom', 'salesforce', 'slack'],
  time: 'Done in 22s'
}, {
  id: 'renewal',
  title: 'Caught a contract expiring Friday',
  body: 'Prepped the renewal and flagged it for your approval.',
  tools: ['hubspot', 'gmail'],
  time: 'Done in 9s'
}, {
  id: 'standup',
  title: 'Built this week’s metrics report',
  body: 'Pulled the numbers and posted the summary to your team.',
  tools: ['stripe', 'googlesheets', 'slack'],
  time: 'Done in 15s'
}];
function ToolChip({
  slug
}: {
  slug: string;
}) {
  return <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-[#F4F0EA] border border-black/[0.05] shrink-0" data-id="element-3243">
      <img src={`https://cdn.simpleicons.org/${slug}`} alt="" aria-hidden="true" className="w-3 h-3 object-contain" loading="lazy" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-3244" />
    </span>;
}
// One "Handled" card, styled to match the reference: peach sparkle badge, bold
// title, supporting line, a row of the tools it touched, and a green Handled
// pill with a timing stat.
function HandledCard({
  item
}: {
  item: HandledItem;
}) {
  return <div className="w-full rounded-3xl bg-white border border-black/[0.06] ring-1 ring-[#EA580C]/[0.08] shadow-[0_30px_70px_-30px_rgba(43,33,26,0.55),0_0_0_1px_rgba(234,88,12,0.04),0_8px_40px_-12px_rgba(234,88,12,0.18)] p-6 md:p-7" data-id="element-3245">
      <div className="flex items-start gap-4" data-id="element-3246">
        {/* Sparkle badge */}
        <span className="flex items-center justify-center w-12 h-12 rounded-full bg-[#FFEAD9] text-[#EA580C] shrink-0" data-id="element-3247">
          <SparklesIcon size={22} strokeWidth={2} data-id="element-3248" />
        </span>
        <div className="flex-1 min-w-0" data-id="element-3249">
          <h3 className="text-lg md:text-xl font-bold text-[#2B211A] leading-snug" data-id="element-3250">
            {item.title}
          </h3>
          <p className="mt-1.5 text-sm md:text-base text-neutral-500 leading-relaxed" data-id="element-3251">
            {item.body}
          </p>
        </div>
      </div>

      {/* Tools touched + Handled pill */}
      <div className="mt-5 flex items-center justify-between gap-3" data-id="element-3252">
        <div className="flex items-center gap-1.5" data-id="element-3253">
          {item.tools.map(slug => <ToolChip key={slug} slug={slug} data-id="element-3254" />)}
        </div>
        <span className="flex items-center gap-1.5 text-sm font-bold text-[#0F9D58] bg-[#0F9D58]/10 rounded-full pl-2.5 pr-3 py-1.5" data-id="element-3255">
          <CheckIcon size={14} strokeWidth={3} data-id="element-3256" />
          Handled
        </span>
      </div>

      {/* Footer stat row */}
      <div className="mt-4 pt-4 border-t border-neutral-100 flex items-center justify-between" data-id="element-3257">
        <span className="flex items-center gap-1.5 text-[11px] font-medium text-neutral-400" data-id="element-3258">
          <ShieldCheckIcon size={13} className="text-[#EA580C]" data-id="element-3259" />
          You stayed in control
        </span>
        <span className="text-[11px] font-semibold text-neutral-400" data-id="element-3260">
          {item.time}
        </span>
      </div>
    </div>;
}
// How long each card holds before the next one slides in.
const CYCLE_MS = 3400;
export function WorkYouCantGetTo() {
  const reduced = !!useReducedMotion();
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => {
      setIndex(i => (i + 1) % HANDLED.length);
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, [reduced]);
  const item = HANDLED[index];
  return <section className="relative w-full overflow-hidden bg-[#1A1410] py-24 md:py-32" data-id="element-3261">
      {/* Dark-surface backdrop — a single soft warm spotlight pooled behind the
             card so the dark stage reads as deliberate and premium. On desktop the
             card sits in the left column, so the spotlight is biased left; on
             mobile the card is centered and the wide falloff still reads as a
             gentle pool of light. No dot grid, no rings — just one clean spotlight. */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true" style={{
      background: 'radial-gradient(46% 60% at 32% 48%, rgba(234,88,12,0.10) 0%, rgba(234,88,12,0.04) 38%, transparent 72%)'
    }} data-id="element-3262" />

      <div className="relative z-10 mx-auto max-w-[1280px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center" data-id="element-3263">
        {/* Visual (Left) — cycling stack of "Handled" cards */}
        <div className="relative w-full flex items-center justify-center order-2 md:order-1 min-h-[360px] md:min-h-[420px]" data-id="element-3264">
          {/* Stacked depth — two faint cards peeking behind so it reads as a
           deck of things Giga has already handled. */}
          <div aria-hidden="true" className="absolute w-[300px] md:w-[380px] h-[200px] rounded-3xl bg-white/70 border border-black/[0.04] shadow-[0_20px_50px_-30px_rgba(43,33,26,0.3)]" style={{
          transform: 'translateY(28px) scale(0.92)'
        }} data-id="element-3265" />
          <div aria-hidden="true" className="absolute w-[300px] md:w-[380px] h-[200px] rounded-3xl bg-white/85 border border-black/[0.05] shadow-[0_24px_60px_-30px_rgba(43,33,26,0.35)]" style={{
          transform: 'translateY(14px) scale(0.96)'
        }} data-id="element-3266" />

          {/* The active card */}
          <div className="relative z-10 w-[300px] md:w-[380px]" data-id="element-3267">
            <AnimatePresence mode="wait" data-id="element-3268">
              <motion.div key={item.id} initial={reduced ? {
              opacity: 0
            } : {
              opacity: 0,
              y: 24,
              scale: 0.96
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={reduced ? {
              opacity: 0
            } : {
              opacity: 0,
              y: -20,
              scale: 0.97
            }} transition={{
              duration: 0.5,
              ease: [0.16, 1, 0.3, 1]
            }} data-id="element-3269">
                <HandledCard item={item} data-id="element-3270" />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Progress dots — show there's a series of examples */}
          <div className="absolute -bottom-2 md:bottom-0 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5" data-id="element-3271">
            {HANDLED.map((h, i) => <button key={h.id} type="button" aria-label={`Show example: ${h.title}`} onClick={() => setIndex(i)} className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? 'w-6 bg-[#EA580C]' : 'w-1.5 bg-white/20 hover:bg-white/40'}`} data-id="element-3272" />)}
          </div>
        </div>

        {/* Text (Right) */}
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative z-10 max-w-xl mx-0 text-left order-1 md:order-2" data-id="element-3273">
          <h2 className="text-3xl md:text-5xl font-title font-medium leading-[1.1] tracking-tight text-[#FAF8F4]" data-id="element-3274">
            You didn't ask, Giga noticed
          </h2>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-white/65 font-medium leading-relaxed" data-id="element-3275">
            Other AI tools wait for instructions. Giga watches your stack, spots
            the work that needs doing, and quietly handles it — then tells you
            what it took care of.
          </p>
        </motion.div>
      </div>
    </section>;
}